import React, { useEffect, useState } from 'react';
import { ReactKeycloakProvider, useKeycloak } from '@react-keycloak/web';
import keycloak from './auth/keycloak';
import App from './Front/App';
import { Box, CircularProgress } from '@mui/material';
import { getData, putData, postData } from './api/awsClient';

function AuthApp() {
  const { keycloak: kc, initialized } = useKeycloak();
  const [redirect, setRedirect] = useState(false);
  const [information, setInformation] = useState('');
  const [credential, setCredential] = useState('');
  const [auth, setAuth] = useState(false);

  useEffect(() => {
    if (!initialized) return;

    // Verificar si el token ya está almacenado en localStorage
    const storedToken = localStorage.getItem('keycloak-token');
    if (storedToken) {
      // Recuperamos el token y configuramos la sesión
      kc.token = storedToken;
      kc.idToken = storedToken;
      kc.authenticated = true;
      // Aquí intentamos parsear el token
      try {
        kc.idTokenParsed = kc.tokenParsed = JSON.parse(atob(storedToken.split('.')[1])); // Parsear el token (si es JWT)
        if (kc.idTokenParsed) {
          checkUser({ kc, setCredential, setInformation, setRedirect, setAuth });
        }
      } catch (error) {
        console.error('Error parsing token:', error);
        kc.login();
      }
    } else if (kc.authenticated) {
      const interval = setInterval(() => {
        kc.updateToken(30).catch(() => {
          console.warn('No se pudo refrescar el token');
        });
      }, 10000);

      checkUser({ kc, setCredential, setInformation, setRedirect, setAuth });

      return () => clearInterval(interval);
    } else {
      kc.login();
    }
  }, [initialized, kc.authenticated]);

  if (redirect) {
    const base = process.env.REACT_APP_KEYCLOAK_BASE_URL;
    const realm = process.env.REACT_APP_KEYCLOAK_REALM;
    const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID;
    const postLogout = process.env.REACT_APP_KEYCLOAK_REDIRECT_URI_LOGOUT;

    const logoutUrl =
      `${base}/realms/${realm}/protocol/openid-connect/logout` +
      `?post_logout_redirect_uri=${encodeURIComponent(postLogout)}` +
      `&client_id=${encodeURIComponent(clientId)}`;

    // Cuando redirect es true, entonces mostramos el componente App
    return (
      <App
        user={information}
        credentials={credential}
        auth={auth}
        logout={logoutUrl}
      />
    );
  }

  // Si aún no está inicializado, mostramos un spinner de carga
  if (!initialized) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
      <CircularProgress />
    </Box>
  );
}

async function checkUser({ kc, setCredential, setInformation, setRedirect, setAuth }) {
  const parsed = kc.idTokenParsed || kc.tokenParsed || {};
  const login = parsed.preferred_username || parsed.email || parsed.sub;
  const country = parsed.locale || '';

  try {
    const response = await getData('users/exist', login, null);
    if (response?.data && response.hasData) {
      const fullName = `${parsed.given_name || ''} ${parsed.middle_name || ''}`.trim();
      const needsUpdate =
        parsed.email !== response.data.Email ||
        fullName !== response.data.Nombre ||
        response.data.Login !== login;

      if (needsUpdate) {
        const user = {
          Login: login,
          Nombre: fullName,
          Email: parsed.email,
          Rol: response.data.profile,
          Country: country || response.data.CountryIds, 
        };
        await putData(`users/information/update/${login}`, user);
      }

      setAuth(!!response.data?.activo);
      setInformation(response.data);
    } else {
      const user = {
        Login: login,
        Nombre: `${parsed.given_name || ''} ${parsed.middle_name || ''}`.trim(),
        Email: parsed.email,
        Country: country,
        Rol: 'None',
      };
      await postData('users/create', user);
      setAuth(false);
      setInformation(user);
    }

    setCredential(kc.idToken);
    setRedirect(true);

    localStorage.setItem('keycloak-token', kc.token);
    localStorage.setItem('keycloak-refresh-token',kc.refreshToken);
    localStorage.setItem('keycloak-idToken', kc.idToken);
  } catch (error) {
    setAuth(false);
    setRedirect(true);
  }
}

export default function Auth() {
  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={{
        onLoad: 'login-required',
        token: localStorage.getItem("keycloak-token"),
        refreshToken: localStorage.getItem("keycloak-refresh-token"),
        checkLoginIframe: false
      }}
      onTokens={({token, refreshToken}) => {
      if(token)
        localStorage.setItem('keycloak-token',token);
      if(refreshToken)
        localStorage.setItem('keycloak-refresh-token',refreshToken)
    }}
    >
      <AuthApp />
    </ReactKeycloakProvider>
  );
}
